import React, { useEffect,useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import classNames from 'classnames';
import _get from 'lodash/get';
import _identity from 'lodash/identity';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _pickBy from 'lodash/pickBy';
import _uniqBy from 'lodash/unionBy';
import _xorWith from 'lodash/xorWith';
import PropTypes from 'prop-types';

import ZpButton from 'components/zpButton';
import { formatCurrency, importStatic, isUserOutsideUS, JOB_BUCKET_GROUP } from 'utils';
import { MEDIAN_SALARY_THRESHOLD } from 'utils/constant';

import AutosuggestProxy from '../../services/autosuggestProxy';
import { jobService } from '../../services/jobService';
import AutosuggestInput from '../autosuggestInput/index';
import ZpChip from '../ZpChip';

import {
  FILTER_PARAMS,
  FILTER_TYPES,
  mapAutoSuggestValue,
  SHOW_AS,
  WORK_FROM_HOME,
} from './filterUtils';
import SalaryFilter from './SalaryFilter';

import zpFilterClasses from './zpFilter.module.css';

const propTypes = {
  type: PropTypes.string.isRequired,
  headerText: PropTypes.string,
  buttonText: PropTypes.string,
  newButtonText: PropTypes.string,
  showAs: PropTypes.string,
  alignRight: PropTypes.bool,
  param: PropTypes.string.isRequired,
  filterOpen: PropTypes.bool,
  filterGroup: PropTypes.string,
  valueRange: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      displayName: PropTypes.string,
    })
  ),
  preselectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      displayName: PropTypes.string,
    })
  ),
  options: PropTypes.object,
  disabled: PropTypes.bool,
  hiddenOnDisabled: PropTypes.bool,
  alwaysShowButtonText: PropTypes.bool,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  onReset: PropTypes.func,
  onUndo: PropTypes.func,
  onFilterClick: PropTypes.func,
  onFilterSelect: PropTypes.func,
  showAsworkFromHome: PropTypes.bool,
  children: PropTypes.node,
  staticSuggestions: PropTypes.arrayOf(PropTypes.object),
  isActive: PropTypes.bool,
};

const defaultProps = {
  headerText: '',
  buttonText: 'Button',
  newButtonText: '',
  alignRight: false,
  showAs: SHOW_AS.BUTTON,
  valueRange: [],
  preselectedItems: [],
  filterEnabledValues: [],
  options: {},
  disabled: false,
  hiddenOnDisabled: false,
  alwaysShowButtonText: false,
  onClear: () => {},
  onApply: () => {},
  onFilterClick: () => {},
  showAsworkFromHome: false,
  children: null,
  staticSuggestions: [],
  isActive: null,
};

const ZpFilter = ({
  type,
  param,
  showAs,
  options,
  isCompact,
  headerText,
  buttonText,
  onInputChange,
  newButtonText,
  valueRange,
  filterOpen,
  alignRight,
  filterGroup,
  preselectedItems,
  filterEnabledValues,
  disabled,
  hiddenOnDisabled,
  alwaysShowButtonText,
  onApply,
  onClear,
  onFilterClick,
  onFilterSelect,
  controlledValues,
  showAsworkFromHome,
  staticSuggestions,
  isActive,
}) => {
  const [open, setOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const criteria = useSelector((state) => state.locals.criteria);

  const [filterValues, setFilterValues] = useState(getInitialFilterValues(undefined, criteria));

  const isFilterOpen = filterOpen !== undefined ? filterOpen : open;

  const enabledValues = filterValues.filter((value) => value.enabled);

  const [button, setButtonText] = useState(getButtonText(preselectedItems));

  const [stateHistory, setStateHistory] = useState([]);

  const [sliderValue, setSliderValue] = useState(
    Array.isArray(filterValues[0]?.value)
      ? filterValues[0]?.value?.[0] || 0
      : filterValues[0]?.value || 0
  );

  const [medianSalary, setMedianSalary] = useState(80000);

  const [percentile90, setPercentile90] = useState(200000);

  const [educationSwitch, setEducationSwitch] = useState(options.educationSwitch);

  useEffect(() => {
    if (options.jobTitle && sliderValue === 0 && param === FILTER_PARAMS.SALARY) {
      async function fetchSalaryForTitle() {
        try {
          const estimatedSalary = await jobService.getEstimateSalaryForSalaryFilter({
            title: options.jobTitle,
          });
          setMedianSalary(estimatedSalary.median);
          setPercentile90(estimatedSalary.percentile);
        } catch (error) {
          console.log(error);
        }
      }
      fetchSalaryForTitle();
    }
  }, []);

  useEffect(() => {
    if (isCompact) {
      setFilterEnabledValues();
    }
  }, []);

  useEffect(() => {
    if (newButtonText) setButtonText(newButtonText);
  }, [newButtonText]);

  useEffect(() => {
    setFilterValues(_uniqBy([...filterValues, ...valueRange], 'displayName'));
  }, [valueRange]);

  useEffect(() => {
    const mappedValues = enabledValues.map(({ value, displayName }) => ({ value, displayName }));
    if (onFilterSelect) {
      onFilterSelect(param, mappedValues);
    }
  }, [filterValues]);

  useEffect(() => {
    if (!isCompact && filterGroup && type !== FILTER_TYPES.MORE) {
      setFilterEnabledValues();
    }
  }, [filterEnabledValues]);

  useEffect(() => {
    if (!filterGroup) {
      setFilterValues(
        _uniqBy(
          [
            ...filterValues,
            ...preselectedItems.map(({ value, displayName }) => ({
              value,
              displayName,
              enabled: true,
            })),
          ],
          'displayName'
        )
      );
    } else if (window.pageCategory !== 'Matches') {
      setFilterValues(getInitialFilterValues(undefined, criteria));
    }
    const initialFilterValue = getInitialFilterValues(filterValues, criteria);
    if (initialFilterValue && initialFilterValue[0] && param === FILTER_PARAMS.SALARY) {
      setSliderValue(
        Array.isArray(filterValues[0]?.value)
          ? filterValues[0]?.value?.[0] || 0
          : filterValues[0]?.value || 0
      );
    }
    if (param === FILTER_PARAMS.EDUCATION && preselectedItems.length) {
      const value = Array.isArray(preselectedItems[0].value)
        ? preselectedItems[0].value[0]
        : preselectedItems[0].value;
      setEducationSwitch(value === 'None' || value === 'High School Diploma');
    }
  }, [preselectedItems]);

  useEffect(() => {
    if (type === FILTER_TYPES.MORE) {
      Object.values(options?.enabledMoreValues || {}).forEach((moreValues) => {
        const changed = stateHistory.length ? hasFilterStateChanged(moreValues) : true;
        if (changed) {
          setStateHistory((prevValues) => [...prevValues, moreValues]);
        }
      });
    } else {
      const changed = stateHistory.length ? hasFilterStateChanged() : true;
      if (changed) {
        setStateHistory([...stateHistory, enabledValues]);
      }
    }
  }, [isFilterOpen]);

  useEffect(() => {
    const oldFilterValues = [...filterValues];
    const index = oldFilterValues.findIndex((filterValue) => filterValue.enabled);
    if (index !== -1 && valueRange[index]) {
      oldFilterValues[index] = {
        ...oldFilterValues[index],
        value: educationSwitch
          ? valueRange[index].value === 'None'
            ? valueRange[index].value
            : ['None', valueRange[index].value]
          : options.autoSuggest
          ? oldFilterValues[index].value
          : valueRange[index].value,
      };
      setFilterValues(oldFilterValues);
    }
  }, [educationSwitch]);

  useEffect(() => {
    if (controlledValues && Array.isArray(controlledValues) && controlledValues.length) {
      const allVals = controlledValues.map((x) => x.value);
      setFilterValues(
        getInitialFilterValues(controlledValues, criteria).map((x) => ({
          ...x,
          enabled: allVals.includes(x.value) || x.forcedOption,
        }))
      );
      setButtonText(controlledValues[0].displayName ?? buttonText);
    }
  }, [controlledValues]);

  function setFilterEnabledValues() {
    const filterValuesCopy = [...filterValues];
    filterValuesCopy.forEach(({ displayName }, idx) => {
      const index = filterEnabledValues.findIndex(
        (filterValue) => filterValue.displayName === displayName
      );
      if (index !== -1) {
        filterValuesCopy[idx].enabled = true;
      } else {
        if (param === FILTER_PARAMS.SALARY) {
          setSliderValue(0);
        }
        filterValuesCopy[idx].enabled = false;
      }
    });
    const enabled = filterValuesCopy.filter(({ enabled }) => enabled);
    setButtonText(getButtonText(enabled));
    setFilterValues(filterValuesCopy);
  }

  function getInitialFilterValues(newValues, criteria) {
    let newValueRange = valueRange.map((filterValue) => {
      const index = preselectedItems.findIndex(
        (preselectedItem) => preselectedItem.displayName === filterValue.displayName
      );
      if (index !== -1) {
        return {
          ...filterValue,
          enabled: true,
        };
      }
      return filterValue;
    });

    newValueRange = newValueRange.map((filterValue) => {
      const index = filterEnabledValues.findIndex(
        (preselectedItem) => preselectedItem.displayName === filterValue.displayName
      );
      if (index !== -1) {
        return {
          ...filterValue,
          enabled: true,
        };
      }
      return filterValue;
    });

    return _uniqBy(
      [
        ...newValueRange,
        ...(newValues || filterEnabledValues).map(({ value, displayName }) => ({
          value,
          displayName,
          enabled: true,
        })),
        ...// Avoid have pre-selected items when user clears them
        (param === FILTER_PARAMS.LOCATION && filterEnabledValues?.length
          ? []
          : preselectedItems.map(({ value, displayName }) => ({
              value,
              displayName,
              enabled: true,
            }))),
        ...(param === FILTER_PARAMS.LOCATION &&
        options.remote &&
        showAsworkFromHome &&
        !isUserOutsideUS(criteria)
          ? [
              {
                value: WORK_FROM_HOME,
                displayName: WORK_FROM_HOME,
              },
            ]
          : []),
      ],
      'displayName'
    );
  }

  function getButtonText(values) {
    return values.length > 1
      ? `${values[0].displayName}, ...`
      : values.length === 1
      ? values[0].displayName
      : buttonText;
  }

  const hasFilterStateChanged = (newValues = enabledValues) => {
    const lastValues = stateHistory[stateHistory.length - 1];
    return !_isEmpty(_xorWith(lastValues, newValues, _isEqual));
  };

  const handleAutosuggestError = (errorObject) => {
    setErrorMessage(errorObject.errorMessage);
  };

  const handleFilterOpen = (isOpen) => {
    if (filterOpen !== undefined) {
      onFilterClick(param);
    } else {
      setOpen(isOpen);
    }
  };

  const handleApply = (newValues = enabledValues) => {
    if (errorMessage) setErrorMessage('');
    handleFilterOpen(false);
    if (type !== FILTER_TYPES.MORE) {
      const mappedValues = newValues.map(({ value, displayName, redirectedLoc, population }) =>
        _pickBy(
          {
            value,
            displayName,
            redirectedLoc,
            population,
          },
          _identity
        )
      );
      if (hasFilterStateChanged(newValues)) {
        setButtonText(getButtonText(newValues));
        onApply(
          param,
          type === FILTER_TYPES.MULTI ? mappedValues : mappedValues.length ? mappedValues[0] : []
        );
      }
    } else {
      onApply();
    }
  };

  const handleAutosuggestChange = (model) => {
    const { isListOpen, suggestionSelected, displayValue, redirectedLoc, population, valueObj } =
      model;

    if (!isListOpen && !displayValue) {
      return;
    }

    if (enabledValues.length >= options.limit) {
      const defaultErrorMessage = `Limit of ${options.limit} selections. Please remove one before adding another.`;
      setErrorMessage(options.errorMessage || defaultErrorMessage);
      return;
    }
    setErrorMessage('');

    let oldFilterValues = [...filterValues];
    const index = oldFilterValues.findIndex(
      (filterValue) => filterValue.displayName === displayValue
    );

    if (!isListOpen && suggestionSelected) {
      if (index !== -1 && type !== FILTER_TYPES.AUTOSUGGEST) {
        if (!oldFilterValues[index].enabled) {
          oldFilterValues[index].enabled = true;
          if (type === FILTER_TYPES.SINGLE) {
            oldFilterValues = oldFilterValues.map((filterValue, idx) => {
              if (index !== idx && filterValue.enabled) {
                filterValue.enabled = false;
              }
              return filterValue;
            });
          }
          setFilterValues(oldFilterValues);
        }
        return;
      }
      const value = mapAutoSuggestValue(param, valueObj);
      if (type === FILTER_TYPES.SINGLE || type === FILTER_TYPES.AUTOSUGGEST) {
        if (onInputChange) {
          if (!value.normalizedTitle) {
            setErrorMessage(options.defaultErrorMessage);
          } else {
            onInputChange(value);
          }
          return;
        }
        handleSingleSelect({
          value,
          displayName: displayValue,
          ...(redirectedLoc ? { redirectedLoc, population } : {}),
        });
      } else {
        const newFilterValues = [
          {
            value,
            displayName: displayValue,
            ...(redirectedLoc ? { redirectedLoc, population } : {}),
            enabled: true,
          },
          ...filterValues,
        ];
        setFilterValues(newFilterValues);
      }
    }
  };

  const handleSingleSelect = (item) => {
    let oldFilterValues = [...filterValues];
    const currentIndex = oldFilterValues.findIndex(
      (filterValue) => filterValue.displayName === item.displayName
    );
    if (currentIndex !== -1 && !oldFilterValues[currentIndex].enabled) {
      const prevIndex = oldFilterValues.findIndex((filterValue) => filterValue.enabled);
      if (prevIndex !== -1) {
        oldFilterValues[prevIndex] = { ...oldFilterValues[prevIndex], enabled: false };
      }
      oldFilterValues[currentIndex] = {
        ...oldFilterValues[currentIndex],
        value:
          educationSwitch && oldFilterValues[currentIndex].value !== 'None'
            ? ['None', valueRange[currentIndex].value]
            : Array.isArray(oldFilterValues[currentIndex].value) &&
              oldFilterValues[currentIndex].value[0] === 'None'
            ? oldFilterValues[currentIndex].value[1]
            : oldFilterValues[currentIndex].value,
        enabled: true,
      };
    } else if (item.enabled) {
      const currentEnabledItemIndex = oldFilterValues.findIndex(
        (filterValue) => filterValue.enabled
      );
      oldFilterValues[currentEnabledItemIndex] = {
        ...oldFilterValues[currentEnabledItemIndex],
        enabled: false,
      };
    } else {
      const currentEnabledItemIndex = oldFilterValues.findIndex(
        (filterValue) => filterValue.enabled
      );
      if (currentEnabledItemIndex !== -1) {
        oldFilterValues[currentEnabledItemIndex] = {
          ...oldFilterValues[currentEnabledItemIndex],
          enabled: false,
        };
      }
      oldFilterValues = [
        {
          ...item,
          enabled: true,
        },
        ...oldFilterValues,
      ];
    }
    if (item.value === 'None') {
      setEducationSwitch(true);
    }
    const newFilterValues = _uniqBy(oldFilterValues, 'displayName');
    const instantFilterValues = newFilterValues
      .filter((value) => value.enabled)
      .map(({ value, displayName }) => ({ value, displayName }));
    if (
      ((options.autoSuggest && type === FILTER_TYPES.SINGLE) ||
        type === FILTER_TYPES.AUTOSUGGEST) &&
      instantFilterValues.length
    ) {
      instantApply(instantFilterValues);
    }
    setFilterValues(newFilterValues);
  };

  const instantApply = (instantFilterValues) => {
    if (errorMessage) setErrorMessage('');
    handleFilterOpen(false);
    setButtonText(getButtonText(...[instantFilterValues]));
    onApply(
      param,
      type === FILTER_TYPES.MULTI
        ? instantFilterValues
        : instantFilterValues.length
        ? instantFilterValues[0]
        : []
    );
  };

  const handleButtonClick = () => {
    if (!disabled) {
      if (options.logEvent && !filterOpen) {
        options.logEvent();
      }
      handleFilterOpen(!open);
    }
  };

  const handleMultiSelect = (item) => {
    if (enabledValues.length >= options.limit && !item.enabled) {
      const defaultErrorMessage = `Limit of ${options.limit} items. Please remove one before selecting another.`;
      setErrorMessage(options.errorMessage || defaultErrorMessage);
      return;
    }
    setErrorMessage('');

    const oldFilterValues = [...filterValues];
    const index = oldFilterValues.findIndex(
      (filterValue) => filterValue.displayName === item.displayName
    );
    if (index !== -1) {
      if (item.enabled) {
        oldFilterValues[index] = { ...oldFilterValues[index], enabled: false };
      } else {
        oldFilterValues[index] = { ...oldFilterValues[index], enabled: true };
      }
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    setFilterValues(oldFilterValues);
  };

  const handleClear = () => {
    if (errorMessage) setErrorMessage('');
    const oldFilterValues = [...filterValues].map(({ value, displayName }) => ({
      value,
      displayName,
    }));
    if (param === FILTER_PARAMS.SALARY) {
      setSliderValue(0);
    }
    handleFilterOpen(false);
    const newEnabledValues = [...filterValues].filter((value) => value.enabled);
    setButtonText(getButtonText([]));
    setFilterValues(oldFilterValues);
    if (type === FILTER_TYPES.MORE) {
      onClear();
    }
    if (newEnabledValues.length) {
      onClear(param, []);
    }
  };

  const handleOverlayClick = () => {
    if (type === FILTER_TYPES.MORE) {
      const filterChanges = Object.values(options?.enabledMoreValues || {}).map((moreValues) =>
        hasFilterStateChanged(moreValues)
      );
      if (filterChanges.some((isChanged) => isChanged)) {
        handleApply();
      }
      handleFilterOpen(false);
    } else if (hasFilterStateChanged()) {
      handleApply();
    } else {
      handleFilterOpen(false);
    }
  };

  const handleSalaryChange = (salary) => {
    setSliderValue(salary);
    setFilterValues([
      {
        value: medianSalary < MEDIAN_SALARY_THRESHOLD ? salary * 2080 : salary,
        displayName: medianSalary < MEDIAN_SALARY_THRESHOLD ? `$${salary}` : formatCurrency(salary),
        enabled: true,
      },
    ]);
  };

  const handleEducationSwitchChange = (e) => {
    setEducationSwitch(e.target.checked);
  };

  const getFilterButtonClassName = () => {
    let className = 'zpBtn';
    if (showAs === SHOW_AS.LINK) {
      className += ` ${zpFilterClasses['zp-filter-link']} zp-filter-link`;
    } else {
      className += ` ${zpFilterClasses['zp-filter-button']}`;
    }
    if (buttonText !== button || isActive) {
      className += ` active`;
    }
    if (filterGroup === JOB_BUCKET_GROUP.MY_PROFILE_JOBS) {
      className += ` ${zpFilterClasses['zp-filter-matches']}`;
    }

    if (isFilterOpen) {
      className += ` open`;
    }
    return className;
  };

  const onEnterKeyDown = (input, matches) => async (e) => {
    const matchesLength = matches.length;
    if (param === FILTER_PARAMS.JOB_TITLE && options.profileJobs && matchesLength <= 1) {
      const { getAutosuggestTitle } = AutosuggestProxy;

      if (!input || !input.displayValue) return;

      const searchValue = input.displayValue;
      const results = await getAutosuggestTitle(searchValue);
      const resultTitle = results.length > 0 ? results[0].name || results[0].value : '';
      const searchTextFinal =
        searchValue.toLowerCase() === resultTitle.toLowerCase() ? resultTitle : searchValue;

      const newValue = {
        value: mapAutoSuggestValue(param, {
          name: resultTitle,
          original_name: searchValue,
        }),
        displayName: searchValue,
        enabled: true,
      };

      const oldFilterValues = [...filterValues];

      const index = oldFilterValues.findIndex(
        (filterValue) => filterValue.displayName === newValue.displayName
      );

      if (searchTextFinal && resultTitle && index === -1) {
        setFilterValues([newValue, ...filterValues]);
      } else if (index !== -1) {
        if (oldFilterValues[index].enabled) {
          oldFilterValues[index] = { ...oldFilterValues[index], enabled: false };
        } else {
          oldFilterValues[index] = { ...oldFilterValues[index], enabled: true };
        }

        setFilterValues(oldFilterValues);
      }
    }
  };

  const renderFilterContent = () => (
    <>
      <div className={zpFilterClasses['filter-header']}>
        {param === FILTER_PARAMS.SALARY && medianSalary < MEDIAN_SALARY_THRESHOLD
          ? 'Minimum Salary Desired Per Hour'
          : headerText}
      </div>
      <div className={zpFilterClasses['filter-body']}>
        {(options.autoSuggest ||
          param === FILTER_PARAMS.LOCATION ||
          param === FILTER_PARAMS.JOB_TITLE) && (
          <AutosuggestInput
            category={param}
            inputProps={{
              className: 'typeahead form-control filter-input',
              name: param,
              ...(options.autoSuggestPlaceholder
                ? {
                    placeholder: options.autoSuggestPlaceholder,
                  }
                : {}),
            }}
            staticSuggestions={param === FILTER_PARAMS.COMPANY_INDUSTRY ? staticSuggestions : []}
            redirectedLoc={options.redirectedLoc}
            autoFocus={options.autoSuggestFocus !== false || !!staticSuggestions?.length}
            clearOnSelect
            onChange={handleAutosuggestChange}
            onErrorCallback={handleAutosuggestError}
            extended
            searchUnitedStates={options.searchUnitedStates}
            onEnterKeyDown={onEnterKeyDown}
            freeTextSupported={options.profileJobs}
          />
        )}

        {param === FILTER_PARAMS.SALARY && (
          <SalaryFilter
            label={options.salaryFieldLabel}
            sliderValue={sliderValue}
            isHourly={medianSalary < MEDIAN_SALARY_THRESHOLD}
            percentile90={percentile90}
            onSalaryChange={(salary) => handleSalaryChange(salary)}
          />
        )}

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        <div className={zpFilterClasses['filter-items']}>
          {type === FILTER_TYPES.SINGLE &&
            param !== FILTER_PARAMS.SALARY &&
            filterValues
              .filter((item) => (options.showOnlyEnabled ? item.enabled : true))
              .map((item) => (
                <div
                  key={item.displayName}
                  className={`filter-option${item.enabled ? ' enable' : ''}`}
                  onClick={() => handleSingleSelect(item)}
                >
                  {item.displayName}
                </div>
              ))}

          {type === FILTER_TYPES.MULTI &&
            filterValues.map((item, idx) => (
              <ZpChip
                key={idx}
                display={item.displayName}
                enable={item.enabled}
                data={item}
                onClick={() => handleMultiSelect(item)}
              />
            ))}
        </div>
        {options.footerText && (
          <div className={zpFilterClasses['footer-text']}>
            {param === FILTER_PARAMS.EDUCATION && (
              <label className={`switch${educationSwitch ? ' checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={educationSwitch}
                  onChange={handleEducationSwitchChange}
                />
                <span className="slider round" />
              </label>
            )}
            <span
              className={`white-space-normal ${param !== FILTER_PARAMS.EDUCATION ? 'ml-3' : ''}`}
            >
              {options.footerText}
            </span>
          </div>
        )}
      </div>
    </>
  );

  if (disabled && hiddenOnDisabled) {
    return null;
  }

  if (type === FILTER_TYPES.AUTOSUGGEST) {
    return (
      <div
        className={classnames(
          zpFilterClasses['filter-auto-suggest-input'],
          'mr-2 position-unset ',
          options.autoSuggestOutClass
        )}
      >
        <AutosuggestInput
          category={param}
          inputProps={{
            className: 'typeahead zp-input zp-input-search',
            name: param,
            ...(options.autoSuggestPlaceholder
              ? {
                  placeholder: options.autoSuggestPlaceholder,
                }
              : {}),
          }}
          prefillValue={preselectedItems?.[0]?.displayName}
          redirectedLoc={options.redirectedLoc}
          onChange={handleAutosuggestChange}
          extended
          searchUnitedStates={options.searchUnitedStates}
          onEnterKeyDown={onEnterKeyDown}
        />
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>
    );
  }

  return (
    <div className={classNames(zpFilterClasses['zp-filter-container'], 'zp-filter-container')}>
      {isFilterOpen && !isCompact && (
        <div
          className={classNames(
            zpFilterClasses['filters-background-overlay'],
            options.noOverlay ? zpFilterClasses['no-overlay'] : ''
          )}
          onClick={handleOverlayClick}
        />
      )}
      {!isCompact && (
        <ZpButton
          theme={buttonText !== button || isActive ? 'primary' : 'secondary'}
          dropdownStatus={isFilterOpen ? 'open' : 'closed'}
          className={getFilterButtonClassName()}
          onClick={handleButtonClick}
        >
          {showAs === SHOW_AS.LINK ? (
            <a
              className={classNames(
                zpFilterClasses['filter-text'],
                `list-link ${isFilterOpen ? ' open' : ''}${disabled ? ' disabled' : ''}`
              )}
            >
              {alwaysShowButtonText ? buttonText : button}
            </a>
          ) : alwaysShowButtonText ? (
            buttonText
          ) : (
            button
          )}
        </ZpButton>
      )}
      {isFilterOpen && (
        <div
          className={classNames(
            zpFilterClasses['zp-filter-dropdown'],
            `zp-filter-dropdown ${alignRight ? ' align-right' : ''}${isCompact ? ' compact' : ''}`
          )}
        >
          <div className={zpFilterClasses['zp-dropdown-menu']}>
            <div
              className={classNames(
                isCompact
                  ? 'd-none'
                  : `d-flex d-lg-none filter-toolbar ${zpFilterClasses['filter-toolbar']}`,
                `justify-content-between align-items-center`
              )}
            >
              <div className="filter-toolbar-start">
                <img
                  src={importStatic('/ui-router/images/select-x-dark.svg')}
                  alt="cross-icon"
                  onClick={handleOverlayClick}
                />
              </div>
              {!options.noClear && (
                <div className="filter-toolbar-end" onClick={handleClear}>
                  Clear
                </div>
              )}
            </div>
            <div className={zpFilterClasses['filter-content']}>{renderFilterContent()}</div>
            <div
              className={
                isCompact
                  ? 'd-none'
                  : `${zpFilterClasses['filter-footer']} ${
                      options.noClear ? ' justify-content-end' : ''
                    }`
              }
            >
              {!options.noClear && (
                <div
                  onClick={handleClear}
                  className={classNames(
                    zpFilterClasses['filter-footer-start'],
                    'd-none d-lg-block '
                  )}
                >
                  Clear
                </div>
              )}
              <div className={zpFilterClasses['filter-footer-end']} onClick={() => handleApply()}>
                Apply
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ZpFilter.propTypes = propTypes;
ZpFilter.defaultProps = defaultProps;

export default ZpFilter;
